import { useId, useMemo } from "react";
import { cn } from "../lib/utils";
import type { RenduReponse, TeinteBambu } from "./contract";
import { COULEUR_UNIQUE, HEXA_UNIQUE, type LigneFilament } from "./silhouettes-filaments";
import { nombreFr } from "./silhouettes-textes";
import { type Colonne, JUSTESSE_MM, type Verdict, verdict } from "./silhouettes-verdict";

/**
 * L'étagère du mode serre-livres (#337, spec section 1) : les deux figurines à l'échelle, chacune
 * dans son Bookend, la barre poussée vers le mur, recolorées aux teintes choisies. Ce qui heurte le
 * mur est en rouge, un débord de justesse en ambre. C'est un SVG qui ne demande rien au serveur :
 * le miroir et l'échange se voient aussitôt, avec les débords déjà mesurés.
 *
 * La vue de face vient du rendu (`face_svg`, écrit par `face.py` dans `products`). Elle est lue par
 * `DOMParser`, jamais insérée telle quelle : seuls la boîte (`viewBox`), le nom de chaque couleur
 * (`data-couleur`) et ses données de chemin (`d`) sont repris, et redessinés en éléments React.
 * Une balise de plus dans ce SVG (script, image, style, objet étranger) n'a nulle part où aller.
 *
 * Les cotes du Bookend sont celles de `Bookend v1.stl`, copié dans `products` (`bookend-v1.json`).
 * Les couleurs neutres (livres, planche, textes) sont des jetons du thème : l'étagère tient dans le
 * thème sombre. Les couleurs des figurines et des Bookends sont celles des filaments : ce sont de
 * vraies teintes, qui ne changent pas avec le thème. Un contour fin, en jeton lui aussi, tient les
 * pièces noires lisibles sur fond sombre et les blanches sur fond clair (`CONTOUR`) ; le rouge et
 * l'ambre sont posés sur un liséré de la couleur de la carte (`LISERE_PX`), pour se lire sur un
 * Bookend de n'importe quelle teinte, le rouge compris.
 *
 * Toutes les cotes sont en millimètres, x vers la droite, y vers le bas, le dessus de la planche
 * à y = 0 : une cote `z` au-dessus de la planche s'écrit `-z`.
 */

/** Le Bookend de Nicolas : un pied de 77 x 4 mm, un mur de 4 mm sur 100 de haut, une semelle de 20 x 1 mm. */
const PIED_LONGUEUR_MM = 77;
const PIED_EPAISSEUR_MM = 4;
const MUR_EPAISSEUR_MM = 4;
const MUR_HAUTEUR_MM = 100;
const SEMELLE_LONGUEUR_MM = 20;
const SEMELLE_EPAISSEUR_MM = 1;

/**
 * Entre les faces des deux murs qui regardent les figurines : 152 mm de livres entre les murs,
 * plus leurs 2 x 4 mm. C'est l'écart de la planche validée par Nicolas.
 */
const ENTRE_FACES_MM = 160;

/** Où est la face du mur de chaque colonne, celle qui regarde la figurine. */
const FACE_X: Record<Colonne, number> = {
  gauche: -ENTRE_FACES_MM / 2,
  droite: ENTRE_FACES_MM / 2,
};

/** Le sens de la figurine à partir de la face de son mur : à gauche du mur de gauche, à droite de l'autre. */
const SENS: Record<Colonne, -1 | 1> = { gauche: -1, droite: 1 };

const COLONNES: readonly Colonne[] = ["gauche", "droite"];

/**
 * Le profil d'un Bookend vu de face, en `[u, z]` : `u` part de la face du mur vers la figurine
 * (négatif dans le mur et sous les livres), `z` est la hauteur au-dessus de la planche. Le pied
 * porte la figurine, le mur retient les livres, la semelle glisse sous eux.
 */
const PROFIL_BOOKEND: readonly (readonly [number, number])[] = [
  [PIED_LONGUEUR_MM, 0],
  [-(MUR_EPAISSEUR_MM + SEMELLE_LONGUEUR_MM), 0],
  [-(MUR_EPAISSEUR_MM + SEMELLE_LONGUEUR_MM), SEMELLE_EPAISSEUR_MM],
  [-MUR_EPAISSEUR_MM, SEMELLE_EPAISSEUR_MM],
  [-MUR_EPAISSEUR_MM, MUR_HAUTEUR_MM],
  [0, MUR_HAUTEUR_MM],
  [0, PIED_EPAISSEUR_MM],
  [PIED_LONGUEUR_MM, PIED_EPAISSEUR_MM],
];

/**
 * Les livres de la planche validée : neuf dos de 15 à 18 mm de large, de 161 à 173 mm de haut,
 * debout sur les semelles. Ils ne sont pas ceux de Nicolas, ils montrent la place que les siens
 * prendront : la planche les nomme « tes livres ».
 */
const LIVRES_DE_LA_PLANCHE: readonly { largeur: number; hauteur: number; opacite: number }[] = [
  { largeur: 16.7, hauteur: 169, opacite: 0.07 },
  { largeur: 15.7, hauteur: 169, opacite: 0.12 },
  { largeur: 17.7, hauteur: 161, opacite: 0.17 },
  { largeur: 14.7, hauteur: 169, opacite: 0.09 },
  { largeur: 16.7, hauteur: 173, opacite: 0.14 },
  { largeur: 15.7, hauteur: 169, opacite: 0.08 },
  { largeur: 17.7, hauteur: 161, opacite: 0.15 },
  { largeur: 16.7, hauteur: 169, opacite: 0.1 },
  { largeur: 17.7, hauteur: 169, opacite: 0.18 },
];

const LIVRES_HAUTEUR_MAX_MM = Math.max(...LIVRES_DE_LA_PLANCHE.map((livre) => livre.hauteur));

/** Les livres, posés de la face intérieure d'un mur à celle de l'autre, séparés d'un filet égal. */
const LIVRES = (() => {
  const entreLesMurs = ENTRE_FACES_MM - 2 * MUR_EPAISSEUR_MM;
  const largeurs = LIVRES_DE_LA_PLANCHE.reduce((somme, livre) => somme + livre.largeur, 0);
  const filet = (entreLesMurs - largeurs) / (LIVRES_DE_LA_PLANCHE.length - 1);
  let x = -entreLesMurs / 2;
  return LIVRES_DE_LA_PLANCHE.map((livre) => {
    const pose = { ...livre, x };
    x += livre.largeur + filet;
    return pose;
  });
})();

/** Le dos d'un livre porte deux bandes plus sombres : une sous le haut, une près du pied. */
const BANDE_HAUT_DEPUIS_LE_HAUT_MM = 26;
const BANDE_HAUT_MM = 4;
const BANDE_BAS_MM = 3;
const BANDE_BAS_DEPUIS_LA_PLANCHE_MM = 16;
const BANDE_OPACITE = 0.1;

/**
 * La bande de la face du mur où l'ambre est dessiné : ce qui est à moins de 2 mm du mur, et jamais
 * moins que la marge de justesse du verdict (`JUSTESSE_MM`, 0,5 mm), qu'elle contient donc toujours :
 * une pointe « de justesse » est dans la bande, quelle que soit la marge qu'on choisira un jour. À
 * 3 px par mm, la marge seule ne ferait qu'un trait de 1,5 px, et l'ambre ne se verrait pas.
 */
const BANDE_AMBRE_MM = Math.max(2, JUSTESSE_MM);

const PLANCHE_MM = 12;
const MARGE_MM = 10;
const TEXTE_MM = 4.6;

/** De quoi écrire « tes livres » au-dessus du plus haut. */
const HAUT_MIN_MM = LIVRES_HAUTEUR_MAX_MM + 9;
const MARGE_HAUT_MM = 4;

/** Le plus grand côté d'une vue de face qu'on accepte : l'outil rend 400 mm de haut au plus. */
const COTE_FACE_MAX_MM = 1000;

/** Les caractères d'une donnée de chemin SVG : commandes, nombres et séparateurs, rien d'autre. */
const DONNEES_DE_CHEMIN = /^[MmZzLlHhVvCcSsQqTtAa0-9eE+.,\s-]+$/;

const HEXADECIMAL = /^#[0-9A-Fa-f]{6}$/;

/** Un trait d'écran : de la même épaisseur à toute échelle (`vector-effect`), aux jonctions rondes. */
const TRAIT_D_ECRAN = { strokeLinejoin: "round", vectorEffect: "non-scaling-stroke" } as const;

/**
 * Le contour d'une pièce : un trait de 2 px d'écran, quelle que soit l'échelle, peint **sous** son
 * aplat (`paint-order`), si bien que seule sa moitié extérieure se voit, un filet de 1 px. Il sépare
 * du fond une pièce noire sur le thème sombre et une pièce blanche sur le thème clair, sans
 * rogner la pièce ni la cerner d'un liséré à l'intérieur.
 */
const CONTOUR = { ...TRAIT_D_ECRAN, strokeWidth: 2, paintOrder: "stroke" } as const;

/**
 * Le rouge et l'ambre : un trait de 2 px, sur un liséré de la couleur de la carte de 6 px, dont 2 px
 * dépassent de chaque côté. Le Bookend a la teinte que Nicolas lui donne : un rouge posé sur un
 * Bookend rouge (PLA Basic Red, une de ses six habituelles) ne se verrait pas sans ce liséré, ni un
 * ambre sur une pièce jaune ; avec lui, le signal se lit sur un mur rouge, noir ou blanc.
 */
const SIGNAL_PX = 2;
const LISERE_PX = 6;

export type Trace = { couleur: string; d: string };

/** La vue de face d'une pièce : sa boîte en mm, et un tracé par couleur, la base d'abord. */
export type Face = { largeur: number; hauteur: number; traces: [Trace, ...Trace[]] };

/**
 * Lit la vue de face d'un rendu (`face_svg`). `null` quand ce n'en est pas une : pas du XML, pas
 * un `svg`, une boîte qui n'est pas de quatre nombres, qui ne part pas de l'origine ou qui n'a pas
 * de taille raisonnable, aucun tracé.
 *
 * Elle ne garde que ce que l'étagère redessine : la boîte, et pour chaque `path` enfant du `svg`
 * son `data-couleur` et son `d`. Tout le reste est lâché, et rien n'est inséré dans la page : le
 * document lu par `DOMParser` est inerte, aucun script n'y tourne. Un `path` sans nom, sans données,
 * dont les données ne sont pas un chemin, ou dont la couleur est déjà prise, est écarté ; un tracé
 * rangé dans un groupe (`g`, `defs`, `clipPath`) n'est pas dessiné par la racine et n'est pas lu.
 */
export function lireFace(svg: string): Face | null {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  } catch {
    return null;
  }
  const racine = doc.documentElement;
  if (racine.localName !== "svg" || doc.getElementsByTagName("parsererror").length > 0) return null;
  const boite = (racine.getAttribute("viewBox") ?? "")
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  const [x0, y0, largeur, hauteur] = boite;
  if (
    boite.length !== 4 ||
    x0 !== 0 ||
    y0 !== 0 ||
    largeur === undefined ||
    hauteur === undefined ||
    !(largeur > 0 && largeur <= COTE_FACE_MAX_MM) ||
    !(hauteur > 0 && hauteur <= COTE_FACE_MAX_MM)
  ) {
    return null;
  }
  const traces: Trace[] = [];
  for (const enfant of Array.from(racine.children)) {
    if (enfant.localName !== "path") continue;
    const couleur = enfant.getAttribute("data-couleur");
    const d = enfant.getAttribute("d")?.trim();
    if (!couleur || !d || !DONNEES_DE_CHEMIN.test(d)) continue;
    if (traces.some((deja) => deja.couleur === couleur)) continue;
    traces.push({ couleur, d });
  }
  const [base, ...suite] = traces;
  return base === undefined ? null : { largeur, hauteur, traces: [base, ...suite] };
}

/** Où est la figurine, et sa barre dans la figurine telle qu'elle est posée (retournée si `miroir`). */
export type Placement = {
  /**
   * Le bord gauche de la boîte de la figurine, en mm depuis la face du mur (celle qui regarde la
   * figurine), positif vers la droite : négatif à gauche du mur de gauche, positif à droite du
   * mur de droite. Retournée, la boîte est la même, c'est son contenu qui change de côté.
   */
  x: number;
  miroir: boolean;
  /** `[x0, x1]` depuis le bord gauche de la boîte : celle du rendu, ou son image dans un miroir. */
  barre: [number, number];
};

/**
 * Où poser une figurine pour que sa barre soit poussée contre le mur : le bout de la barre côté mur
 * est à `place` de la face du mur, jamais plus près (la fente du pied s'arrête là). Le mur est à
 * droite de la figurine dans la colonne de gauche, à sa gauche dans celle de droite ; un miroir
 * retourne la figurine et sa barre autour de la boîte, et le bout côté mur est alors l'autre.
 */
export function placement(
  face: Pick<Face, "largeur">,
  barre: readonly [number, number],
  colonne: Colonne,
  miroir: boolean,
  place: number,
): Placement {
  const [debut, fin] = barre;
  const posee: [number, number] = miroir
    ? [face.largeur - fin, face.largeur - debut]
    : [debut, fin];
  const x = colonne === "gauche" ? -place - posee[1] : place - posee[0];
  return { x, miroir, barre: posee };
}

/** La barre est dans la boîte, de gauche à droite, et elle a une longueur (à un arrondi près). */
function barreValide(barre: readonly [number, number], largeur: number): boolean {
  const [debut, fin] = barre;
  return (
    Number.isFinite(debut) &&
    Number.isFinite(fin) &&
    debut >= 0 &&
    debut < fin &&
    fin <= largeur + 0.01
  );
}

/** Trois décimales : les nombres du SVG ne portent pas le bruit des flottants. */
function arrondi(valeur: number): number {
  return Math.round(valeur * 1000) / 1000;
}

function hexaSur(valeur: string | null | undefined): string | null {
  return typeof valeur === "string" && HEXADECIMAL.test(valeur) ? valeur : null;
}

/**
 * Ce que l'étagère sait d'une figurine : son rendu (le rapport et la vue de face, tels que
 * `POST /api/silhouettes/rendu` les rend) et le miroir de sa colonne.
 */
export type FigureEtagere = Pick<RenduReponse, "rapport" | "face_svg"> & { miroir: boolean };

/** La figurine posée : sa vue de face lue, et où elle est. */
type Posee = { face: Face; placement: Placement };

/** Une colonne qui a un rendu : son verdict, et sa figurine si la vue de face permet de la poser. */
type ColonneRendue = { figure: FigureEtagere; verdict: Verdict; posee: Posee | null };

function lireColonne(
  colonne: Colonne,
  figure: FigureEtagere | null,
  face: Face | null,
  place: number | null | undefined,
): ColonneRendue | null {
  if (figure === null) return null;
  const rapport =
    place == null ? figure.rapport : { ...figure.rapport, serre_livre_degagement_mm: place };
  const degagement = rapport.serre_livre_degagement_mm;
  const barre = rapport.barre_mm;
  const posee =
    face !== null && degagement != null && barre != null && barreValide(barre, face.largeur)
      ? { face, placement: placement(face, barre, colonne, figure.miroir, degagement) }
      : null;
  return { figure, verdict: verdict(rapport, colonne, figure.miroir), posee };
}

/** Ce que l'étagère signale d'une colonne : ce qui heurte le mur, ou frôle son dégagement. */
function signalDe(verdictDeLaColonne: Verdict): "heurte" | "justesse" | null {
  if (verdictDeLaColonne.etat === "retourner" || verdictDeLaColonne.etat === "aucun-sens") {
    return "heurte";
  }
  return verdictDeLaColonne.etat === "entre" && verdictDeLaColonne.justesse ? "justesse" : null;
}

function cotes(verdictDeLaColonne: { debord: number; place: number }): string {
  const { debord, place } = verdictDeLaColonne;
  return `${nombreFr(debord, 2)} mm côté mur pour ${nombreFr(place, 2)} mm de place`;
}

/** Ce que dit le texte alternatif d'un Bookend : `null` quand la colonne n'a pas de rendu. */
function decrire(rendue: ColonneRendue | null): string {
  if (rendue === null) return "vide";
  const v = rendue.verdict;
  switch (v.etat) {
    case "sans-mesure":
      return "figurine sans mesure du mur";
    case "entre":
      return `la figurine entre${v.justesse ? " de justesse" : ""}, ${cotes(v)}`;
    case "retourner":
      return `la figurine heurte le mur, ${cotes(v)} ; retournée, elle entre`;
    case "aucun-sens":
      return `la figurine heurte le mur, ${cotes(v)}, et n'entre dans aucun sens`;
  }
}

/**
 * La teinte d'un tracé : la teinte choisie pour cette couleur dans cette colonne ; à défaut (le
 * catalogue n'est pas arrivé), la couleur trouvée dans l'image, que le rapport nomme ; à défaut,
 * le gris de l'outil pour la pièce d'une seule couleur. `null` : un ton neutre du thème.
 * Rien n'est repris du fichier de la vue de face, et rien ne passe sans être un hexadécimal.
 */
function hexaDeLaTrace(
  colonne: Colonne,
  couleur: string,
  figure: FigureEtagere,
  teintes: readonly LigneFilament[],
): string | null {
  const choisie = teintes.find((ligne) => ligne.colonne === colonne && ligne.couleur === couleur);
  const trouvee = figure.rapport.couleurs?.find((candidate) => candidate.nom === couleur);
  return (
    hexaSur(choisie?.choisie.hexa) ??
    hexaSur(trouvee?.hexa) ??
    (couleur === COULEUR_UNIQUE ? HEXA_UNIQUE : null)
  );
}

function useFace(figure: FigureEtagere | null): Face | null {
  const svg = figure?.face_svg;
  return useMemo(() => (svg ? lireFace(svg) : null), [svg]);
}

/** Le tracé du Bookend d'une colonne : son profil, la face du mur à `FACE_X`. */
function profilDuBookend(colonne: Colonne): string {
  const points = PROFIL_BOOKEND.map(([u, z]) => `${FACE_X[colonne] + SENS[colonne] * u} ${-z}`);
  return `M${points.join(" L")}Z`;
}

/** Une bande de la scène, de `de` à `vers` mm de la face du mur, positifs du côté de la figurine. */
function bandeDuMur(colonne: Colonne, de: number, vers: number): { x: number; largeur: number } {
  const a = FACE_X[colonne] + SENS[colonne] * de;
  const b = FACE_X[colonne] + SENS[colonne] * vers;
  return { x: Math.min(a, b), largeur: Math.abs(b - a) };
}

/** Où va le dessin d'une figurine : sa boîte à sa place, le bas sur la planche, retournée si `miroir`. */
function transformation(colonne: Colonne, posee: Posee): string {
  const gauche = arrondi(FACE_X[colonne] + posee.placement.x);
  const haut = -posee.face.hauteur;
  if (!posee.placement.miroir) return `translate(${gauche} ${haut})`;
  return `translate(${arrondi(gauche + posee.face.largeur)} ${haut}) scale(-1 1)`;
}

function Livres() {
  return (
    <g data-livres="">
      {LIVRES.map((livre) => (
        <g key={livre.x}>
          <rect
            x={livre.x}
            y={-livre.hauteur}
            width={livre.largeur}
            height={livre.hauteur - SEMELLE_EPAISSEUR_MM}
            className="fill-foreground"
            fillOpacity={livre.opacite}
          />
          <rect
            x={livre.x}
            y={-(livre.hauteur - BANDE_HAUT_DEPUIS_LE_HAUT_MM)}
            width={livre.largeur}
            height={BANDE_HAUT_MM}
            className="fill-foreground"
            fillOpacity={BANDE_OPACITE}
          />
          <rect
            x={livre.x}
            y={-BANDE_BAS_DEPUIS_LA_PLANCHE_MM}
            width={livre.largeur}
            height={BANDE_BAS_MM}
            className="fill-foreground"
            fillOpacity={BANDE_OPACITE}
          />
        </g>
      ))}
    </g>
  );
}

function Bookend({ colonne, teinte }: { colonne: Colonne; teinte: string | null }) {
  return (
    <path
      data-bookend={colonne}
      d={profilDuBookend(colonne)}
      fill={teinte ?? undefined}
      className={cn("stroke-muted-foreground", teinte === null && "fill-muted-foreground")}
      {...CONTOUR}
    />
  );
}

function Figure({
  colonne,
  rendue,
  posee,
  teintes,
}: {
  colonne: Colonne;
  rendue: ColonneRendue;
  posee: Posee;
  teintes: readonly LigneFilament[];
}) {
  return (
    <g
      data-figure={colonne}
      data-miroir={String(posee.placement.miroir)}
      transform={transformation(colonne, posee)}
    >
      {posee.face.traces.map((trace, rang) => {
        const hexa = hexaDeLaTrace(colonne, trace.couleur, rendue.figure, teintes);
        // La base est la silhouette entière, barre comprise : son contour est le bord de la
        // figurine. Les aplats des autres couleurs, peints après, le recouvrent à l'intérieur.
        const contour = rang === 0;
        return (
          <path
            key={trace.couleur}
            data-couleur={trace.couleur}
            d={trace.d}
            fill={hexa ?? undefined}
            fillRule="evenodd"
            className={
              cn(hexa === null && "fill-muted-foreground", contour && "stroke-muted-foreground") ||
              undefined
            }
            {...(contour ? CONTOUR : {})}
          />
        );
      })}
    </g>
  );
}

/**
 * Le tracé de base de la figurine, rejoué à sa place dans le rouge ou l'ambre, sous une coupe qui
 * ne garde que la boîte du mur (ce qui le heurte) ou la bande de sa face (ce qui la frôle). La
 * coupe est posée sur un groupe sans transformation : elle est dans le repère de la scène. Un
 * liséré de la couleur de la carte passe dessous (`LISERE_PX`), coupé de même.
 */
function Signal({
  colonne,
  etat,
  posee,
  idCoupe,
}: {
  colonne: Colonne;
  etat: "heurte" | "justesse";
  posee: Posee;
  idCoupe: string;
}) {
  const [base] = posee.face.traces;
  return (
    <g clipPath={`url(#${idCoupe})`} data-signal={etat} data-colonne={colonne}>
      <g transform={transformation(colonne, posee)}>
        <path
          data-lisere=""
          d={base.d}
          fill="none"
          className="stroke-card"
          strokeWidth={LISERE_PX}
          {...TRAIT_D_ECRAN}
        />
        <path
          d={base.d}
          fillRule="evenodd"
          className={
            etat === "heurte"
              ? "fill-destructive stroke-destructive"
              : "fill-jugement-defaut stroke-jugement-defaut"
          }
          strokeWidth={SIGNAL_PX}
          {...TRAIT_D_ECRAN}
        />
      </g>
    </g>
  );
}

/**
 * L'étagère : les deux Bookends, les livres entre eux, et la figurine de chaque colonne qui a un
 * rendu, posée barre contre le mur, recolorée aux teintes choisies. Ce qui entre dans le mur est
 * peint en rouge, ce qui le frôle (verdict « de justesse ») en ambre : les deux viennent de
 * `verdict` (`silhouettes-verdict`), le même juge que les cartes de la page, et de la géométrie du
 * tracé, rejoué dans une coupe. Le texte alternatif dit les deux verdicts.
 *
 * Elle prend ce que la page a déjà, sans rien calculer pour elle : `gauche` et `droite` sont la
 * réponse du rendu de chaque colonne (rapport et `face_svg`) plus le miroir de la colonne, `null`
 * quand la colonne n'a pas de rendu (son Bookend est alors dessiné vide) ; `teintes` est la liste
 * des lignes de la carte des filaments. Rien ne se refait côté serveur : changer un miroir, une
 * teinte ou échanger les colonnes redessine l'étagère aussitôt.
 */
export function Etagere({
  gauche,
  droite,
  teintes,
  teinteBase,
  place,
  className,
}: {
  /** Le rendu de la figurine de gauche, avec son miroir ; `null` tant que la colonne n'a pas de rendu. */
  gauche: FigureEtagere | null;
  /** Celui de la figurine de droite. */
  droite: FigureEtagere | null;
  /**
   * Les lignes de la carte des filaments (`lignes` de `silhouettes-filaments`) : la teinte choisie
   * pour chaque couleur de chaque figurine. Vide tant que le catalogue n'est pas arrivé : les
   * figurines gardent alors les couleurs trouvées dans leur image.
   */
  teintes: readonly LigneFilament[];
  /** La teinte des Bookends et des Socles. Sans elle, les Bookends sont d'un gris neutre du thème. */
  teinteBase?: TeinteBambu | null | undefined;
  /**
   * Le dégagement du mur, en mm, imposé aux deux figurines. Par défaut : celui du rapport de
   * chacune (`serre_livre_degagement_mm`, 6,63 mm pour le Bookend v1), là où le verdict le lit.
   */
  place?: number | null | undefined;
  /** Les classes du SVG, par exemple une largeur minimale pour défiler sur un téléphone. */
  className?: string | undefined;
}) {
  // Un identifiant de React 19.3 est sûr dans un `url(#...)` (`_r_0_`) : le test des identifiants
  // garde ce format.
  const id = useId();
  const faces: Record<Colonne, Face | null> = { gauche: useFace(gauche), droite: useFace(droite) };
  const rendues: Record<Colonne, ColonneRendue | null> = {
    gauche: lireColonne("gauche", gauche, faces.gauche, place),
    droite: lireColonne("droite", droite, faces.droite, place),
  };
  const teinteDesBookends = hexaSur(teinteBase?.hexa);

  // La scène tient les Bookends, et s'élargit ou monte pour une figurine qui dépasse du pied ou des
  // livres. Elle reste centrée sur les livres : le miroir ne la décale pas.
  let bordGauche = FACE_X.gauche - PIED_LONGUEUR_MM;
  let bordDroit = FACE_X.droite + PIED_LONGUEUR_MM;
  let haut = HAUT_MIN_MM;
  for (const colonne of COLONNES) {
    const posee = rendues[colonne]?.posee;
    if (!posee) continue;
    const debut = FACE_X[colonne] + posee.placement.x;
    bordGauche = Math.min(bordGauche, debut);
    bordDroit = Math.max(bordDroit, debut + posee.face.largeur);
    haut = Math.max(haut, posee.face.hauteur + MARGE_HAUT_MM);
  }
  const demi = Math.max(-bordGauche, bordDroit) + MARGE_MM;

  const description = `Étagère vue de face. Bookend de gauche : ${decrire(rendues.gauche)}. Bookend de droite : ${decrire(rendues.droite)}.`;
  const signaux = COLONNES.flatMap((colonne) => {
    const rendue = rendues[colonne];
    const etat = rendue ? signalDe(rendue.verdict) : null;
    return rendue?.posee && etat ? [{ colonne, etat, posee: rendue.posee }] : [];
  });

  return (
    <svg
      role="img"
      aria-label={description}
      viewBox={`${arrondi(-demi)} ${arrondi(-haut)} ${arrondi(2 * demi)} ${arrondi(haut + PLANCHE_MM)}`}
      preserveAspectRatio="xMidYMax meet"
      className={cn("block h-auto w-full", className)}
    >
      <defs>
        {signaux.map(({ colonne, etat }) => {
          // Ce qui heurte : la boîte du mur. Ce qui frôle : la bande de la face où l'ambre se dessine.
          const bande =
            etat === "heurte"
              ? bandeDuMur(colonne, 0, -MUR_EPAISSEUR_MM)
              : bandeDuMur(colonne, 0, BANDE_AMBRE_MM);
          return (
            <clipPath key={colonne} id={`${id}-${etat}-${colonne}`}>
              <rect x={bande.x} y={-MUR_HAUTEUR_MM} width={bande.largeur} height={MUR_HAUTEUR_MM} />
            </clipPath>
          );
        })}
      </defs>

      <rect
        x={-demi}
        y={0}
        width={2 * demi}
        height={PLANCHE_MM}
        className="fill-foreground"
        fillOpacity={0.1}
      />
      <Livres />
      {COLONNES.map((colonne) => {
        const rendue = rendues[colonne];
        const posee = rendue?.posee;
        return rendue && posee ? (
          <Figure key={colonne} colonne={colonne} rendue={rendue} posee={posee} teintes={teintes} />
        ) : null;
      })}
      {COLONNES.map((colonne) => (
        <Bookend key={colonne} colonne={colonne} teinte={teinteDesBookends} />
      ))}
      {signaux.map(({ colonne, etat, posee }) => (
        <Signal
          key={colonne}
          colonne={colonne}
          etat={etat}
          posee={posee}
          idCoupe={`${id}-${etat}-${colonne}`}
        />
      ))}

      <g fontSize={TEXTE_MM} textAnchor="middle">
        <text x={0} y={-(LIVRES_HAUTEUR_MAX_MM + 3.5)} className="fill-muted-foreground">
          tes livres
        </text>
        <text
          x={FACE_X.gauche - PIED_LONGUEUR_MM / 2}
          y={PLANCHE_MM / 2 + 1.6}
          className="fill-foreground"
        >
          Bookend gauche
        </text>
        <text
          x={FACE_X.droite + PIED_LONGUEUR_MM / 2}
          y={PLANCHE_MM / 2 + 1.6}
          className="fill-foreground"
        >
          Bookend droit
        </text>
      </g>
    </svg>
  );
}
